/**
 * Stroke rendering shared by the live canvas (DrawingCanvas.js) and the PDF
 * export (notebookPdfExport.js). Pure canvas drawing: no React, no component
 * state, so the export draws exactly what the editor draws.
 */

// A stroke as the backend returns it, in the shape the canvas draws
export const toCanvasStroke = (stroke) => ({
    id: stroke.id,
    type: stroke.tool_type,
    tool: stroke.tool_type,
    text: stroke.text_content || undefined,
    style: {
        color: stroke.color,
        thickness: stroke.width,
        opacity: stroke.opacity || stroke.stroke_data?.style?.opacity || 1.0
    },
    points: stroke.stroke_data.points || [],
    created_at: stroke.created_at
});

// Build smooth curves using quadratic Bezier curves into a Path2D
export const buildSmoothPath = (path, points) => {
    if (points.length < 3) return;
    
    // Start at the first point
    path.moveTo(points[0].x, points[0].y);
    
    // Draw curves between
    for (let i = 1; i < points.length - 1; i++) {
        const currentPoint = points[i];
        const nextPoint = points[i + 1];
        
        // Calculate control point as the midpoint between current and next
        const controlX = (currentPoint.x + nextPoint.x) / 2;
        const controlY = (currentPoint.y + nextPoint.y) / 2;
        
        // Draw quadratic curve from current point to control point
        path.quadraticCurveTo(currentPoint.x, currentPoint.y, controlX, controlY);
    }
    
    // Draw final line to the last point
    const lastPoint = points[points.length - 1];
    path.lineTo(lastPoint.x, lastPoint.y);
};

/**
 * Draw a single stroke with smooth curves.
 *
 * pathCache is the live canvas's PathCache (see canvasOptimizations.js); pass
 * null to draw without caching, as the PDF export does.
 */
export const renderStroke = (ctx, stroke, pathCache = null) => {
    // Handle text strokes differently
    if (stroke.type === 'text') {
        // Only render if there's actual text content
        if (!stroke.text || stroke.text.trim() === '') {
            return; // Skip rendering empty or undefined text
        }
        
        ctx.save();
        
        // Apply rotation if the stroke has a rotation angle
        if (stroke.rotation) {
            const point = stroke.points[0];
            ctx.translate(point.x, point.y);
            ctx.rotate(stroke.rotation);
            ctx.translate(-point.x, -point.y);
        }
        
        ctx.fillStyle = stroke.style.color;
        ctx.font = `${stroke.style.thickness * 8}px Arial`; // Use thickness for font size
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        
        // Adjust opacity based on sync status
        let opacity = stroke.style.opacity || 1;
        if (stroke.isPending) {
            opacity = opacity * 0.7; // Make pending text more transparent
        } else if (stroke.isFailed) {
            opacity = opacity * 0.5; // Make failed text even more transparent
        }
        ctx.globalAlpha = opacity;
        
        // Add visual indicator for failed text
        if (stroke.isFailed) {
            ctx.strokeStyle = stroke.style.color;
            ctx.lineWidth = 1;
            ctx.setLineDash([2, 2]);
            ctx.strokeText(stroke.text, stroke.points[0].x, stroke.points[0].y);
        }
        
        ctx.fillText(stroke.text, stroke.points[0].x, stroke.points[0].y);
        ctx.restore();
        return;
    }
    
    // Regular stroke handling
    if (!stroke.points || stroke.points.length < 2) return;
    
    ctx.save();
    ctx.strokeStyle = stroke.style.color;
    ctx.lineWidth = stroke.style.thickness;
    
    // Adjust opacity based on sync status
    let opacity = stroke.style.opacity;
    if (stroke.isPending) {
        opacity = opacity * 0.7; // Make pending strokes more transparent
    } else if (stroke.isFailed) {
        opacity = opacity * 0.5; // Make failed strokes even more transparent
    }
    
    ctx.globalAlpha = opacity;
    
    // Set line cap and join styles - use stroke style if available, otherwise default to round
    ctx.lineCap = stroke.style.lineCap || 'round';
    ctx.lineJoin = stroke.style.lineJoin || 'round';
    
    // Add visual indicator for failed strokes
    if (stroke.isFailed) {
        ctx.setLineDash([5, 5]); // Dashed line for failed strokes
    }
    
    // Phase 3: Path Caching - Check cache first to avoid recalculating curves
    let path = null;
    if (pathCache && !stroke.isPending && !stroke.isFailed) {
        // Only cache stable (non-pending) strokes
        path = pathCache.get(stroke);
    }
    
    if (path) {
        // Use cached path - FAST!
        ctx.stroke(path);
    } else {
        // Calculate path and cache it for next time
        const newPath = new Path2D();
        
        if (stroke.points.length === 1) {
            // Single point - draw a small dot
            const point = stroke.points[0];
            newPath.arc(point.x, point.y, stroke.style.thickness / 2, 0, 2 * Math.PI);
            ctx.fill(newPath);
        } else if (stroke.points.length === 2) {
            // Two points - draw straight line
            newPath.moveTo(stroke.points[0].x, stroke.points[0].y);
            newPath.lineTo(stroke.points[1].x, stroke.points[1].y);
            ctx.stroke(newPath);
        } else {
            // Use smooth quadratic curves for natural-looking strokes
            buildSmoothPath(newPath, stroke.points);
            ctx.stroke(newPath);
        }
        
        // Cache the path for next time (if stable stroke)
        if (pathCache && !stroke.isPending && !stroke.isFailed) {
            pathCache.set(stroke, newPath);
        }
    }
    
    ctx.restore();
};
